import { useState } from "react";
import type {
  CancerDiagnosisEntry,
  DocumentType,
  Patient,
} from "../../../../types";
import { toBackendDocType } from "../../../../api/transformers";
import {
  resolveScale,
  resolveStatus,
  statusSeverity,
} from "../../../../data/recistScales";
import { CancerHoverCell, OverflowChip } from "./CancerHoverCell";

/**
 * Resolve the patient's landing cancer list — the FHIR-Conditions cancers, falling
 * back to a single entry built from the row's dxCode/diagnosis.
 *
 * Exported because it is the single source of truth for "what the Cancer Diagnosis
 * Code + Stage columns show": `PatientTable` sorts through it too, so a row that
 * renders "—" also sorts as empty. Reading `dxCode` directly in a `sortAccessor`
 * silently reintroduces that split — `dxCode` falls back to the MD-note code, which
 * may be a NON-cancer ICD this function deliberately refuses to display.
 *
 * @param patient - The patient row
 * @returns The cancers driving the Diagnosis + Stage columns
 */
export function landingCancers(patient: Patient): CancerDiagnosisEntry[] {
  // The Diagnosis + Cancer Stage columns show ONLY malignant-neoplasm ('C') codes from
  // FHIR Conditions. Non-cancer patients (non-'C' primary) show a dash — never their non-'C'
  // code. The single-'C'-dxCode fallback is load-bearing, not legacy: it covers patients whose
  // cancer is named only in the MD note, where the backend puts that 'C' code on `dxCode` and
  // leaves `conditionCancers` empty.
  if (patient.conditionCancers?.length) return patient.conditionCancers;
  if (patient.dxCode?.toUpperCase().startsWith("C")) {
    return [
      {
        code: patient.dxCode,
        description: patient.diagnosisShort || patient.diagnosis || "",
        stage: "",
      },
    ];
  }
  return [];
}

/**
 * Diagnosis cell: leads with the first cancer's name + ICD code (with a "+N" overflow),
 * and on hover shows a Diagnosis vs. ICD-10 table for all of the patient's cancers.
 *
 * @param patient - The patient row.
 * @returns The diagnosis cell content
 */
export function DiagnosisCell({ patient }: { patient: Patient }) {
  return (
    <CancerHoverCell
      cancers={landingCancers(patient)}
      columnLabel="ICD-10"
      getValue={(c) => c.code}
      lead={(first, extra) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm" style={{ color: "#111827" }}>
            {first.description || first.code}
            <OverflowChip extra={extra} />
          </span>
          {first.code ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold w-fit"
              style={{
                backgroundColor:
                  "var(--color-clinical-treatment-light, #EFF6FF)",
                color: "var(--color-clinical-treatment-dark, #1E40AF)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {first.code}
            </span>
          ) : null}
        </div>
      )}
    />
  );
}

/**
 * Stage cell: leads with the first cancer's stage (with "+N"), and on hover shows a
 * Diagnosis vs. Stage table for all of the patient's cancers. Empty stages render "-".
 *
 * @param patient - The patient row.
 * @returns The stage cell content
 */
export function StageCell({ patient }: { patient: Patient }) {
  return (
    <CancerHoverCell
      cancers={landingCancers(patient)}
      columnLabel="STAGE"
      getValue={(c) => (c.stage ? formatStageLabel(c.stage) : "")}
      lead={(first, extra) => {
        const label = first.stage ? formatStageLabel(first.stage) : "";
        return (
          <span
            className="text-sm font-medium"
            style={{ color: label ? "#111827" : "#9CA3AF" }}
          >
            {label || "-"}
            <OverflowChip extra={extra} />
          </span>
        );
      }}
    />
  );
}

/**
 * Condense a free-text cancer stage to its AJCC stage-group label — e.g.
 * "Clinical Stage IIIC (AJCC v8); pathological yT1aN1mi post-neoadjuvant"
 * becomes "Stage IIIC". Falls back to the raw text (CSS-truncated in the badge)
 * when no recognizable stage group is present.
 *
 * @param raw - The raw stage string (often free-text from FHIR).
 * @returns A concise, standardized stage-group label.
 */
function formatStageLabel(raw: string): string {
  const grouped = raw.match(/stage\s+(0|IV|III|II|I)([ABC])?/i);
  if (grouped)
    return `Stage ${grouped[1].toUpperCase()}${(grouped[2] || "").toUpperCase()}`;
  const roman = raw.match(/\b(IV|III|II|I)([ABC])?\b/);
  if (roman) return `Stage ${roman[1]}${(roman[2] || "").toUpperCase()}`;
  return raw;
}

/**
 * Stage badge, color-coded by stage severity (IV > III > earlier) using the
 * clinical design tokens. Displays the condensed AJCC stage-group label and
 * caps its width (ellipsis + tooltip) so a long free-text stage can never
 * overflow into the adjacent column.
 *
 * @param patient - The patient row.
 * @returns The stage badge
 */
export function StageBadge({ patient }: { patient: Patient }) {
  if (!patient.stage || !patient.stage.trim()) {
    return (
      <span className="text-sm font-medium" style={{ color: "#9CA3AF" }}>
        —
      </span>
    );
  }
  const label = formatStageLabel(patient.stage);
  const isStageIV = label.includes("IV");
  const isStageIII = !isStageIV && label.includes("III");
  const bg = isStageIV
    ? "var(--color-clinical-critical-light)"
    : isStageIII
      ? "var(--color-clinical-progression-light)"
      : "var(--color-clinical-treatment-light)";
  const color = isStageIV
    ? "var(--color-clinical-critical-dark)"
    : isStageIII
      ? "var(--color-clinical-progression-dark)"
      : "var(--color-clinical-treatment-dark)";
  return (
    <span
      className="px-2.5 py-1 text-xs font-semibold rounded-md"
      style={{
        display: "inline-block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        backgroundColor: bg,
        color,
        border: `1px solid ${color}`,
        boxShadow: "0 1px 1px rgba(16,24,40,0.04)",
      }}
      title={patient.stage}
    >
      {label}
    </span>
  );
}

/**
 * Trials cell: chips for each matched trial, or a dash when none.
 *
 * @param patient - The patient row.
 * @returns The trials cell content
 */
export function TrialsCell({ patient }: { patient: Patient }) {
  if (patient.trials.length === 0) {
    return (
      <span className="text-xs font-medium" style={{ color: "#000000" }}>
        -
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1 justify-center">
      {patient.trials.map((trial, index) => (
        <span
          key={index}
          className="inline-flex px-2 py-1 text-xs font-medium rounded"
          style={{
            backgroundColor: "var(--color-clinical-treatment-light)",
            color: "var(--color-clinical-treatment-dark)",
          }}
        >
          {trial}
        </span>
      ))}
    </div>
  );
}

// Disease-status pill/badge colors, keyed by response severity.
const _SEVERITY_COLORS: Record<string, { bg: string; color: string }> = {
  progressive: { bg: "#FEE2E2", color: "#991B1B" },
  stable: { bg: "#F3F4F6", color: "#4B5563" },
  response: { bg: "#D1FAE5", color: "#065F46" },
  neutral: {
    bg: "var(--color-secondary)",
    color: "var(--color-secondary-foreground)",
  },
};

/**
 * Patient-status cell: an optional workflow-status pill plus the disease-status
 * pill. The disease-status pill is mapped — via the patient's ICD-10 cohort — to
 * a response scale (RECIST / IMWG / iwCLL / …); hovering it reveals the scale
 * badge, status label, and the criteria description for that ICD + status combo.
 *
 * @param patient - The patient row.
 * @returns The status cell content
 */
export function StatusCell({ patient }: { patient: Patient }) {
  const [hover, setHover] = useState<{ top: number; left: number } | null>(
    null,
  );
  if (!patient.patientStatus && !patient.diseaseStatus) {
    return (
      <span className="text-xs font-medium" style={{ color: "#9CA3AF" }}>
        —
      </span>
    );
  }
  const scale = resolveScale(patient.dxCode);
  const status = patient.diseaseStatus
    ? resolveStatus(scale, patient.diseaseStatus)
    : null;
  const pill = _SEVERITY_COLORS[statusSeverity(status?.code)];

  const openHover = (e: React.MouseEvent) => {
    if (!status) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHover({
      top: rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
    });
  };

  return (
    <div className="flex flex-col gap-1 items-center">
      {patient.patientStatus && (
        <span
          className="inline-flex px-2 py-0.5 text-xs font-medium rounded w-fit whitespace-nowrap"
          style={{ backgroundColor: "var(--color-primary)", color: "#ffffff" }}
        >
          {patient.patientStatus}
        </span>
      )}
      {patient.diseaseStatus && (
        <span
          className="inline-flex px-2 py-0.5 text-xs font-medium rounded w-fit whitespace-nowrap"
          style={{
            backgroundColor: pill.bg,
            color: pill.color,
            cursor: status ? "help" : "default",
          }}
          onMouseEnter={openHover}
          onMouseLeave={() => setHover(null)}
        >
          {patient.diseaseStatus}
        </span>
      )}
      {hover && status && (
        <div
          style={{
            position: "fixed",
            top: hover.top,
            left: hover.left,
            zIndex: 9999,
            width: 320,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: "12px 14px",
            pointerEvents: "none",
            textAlign: "left",
          }}
        >
          <span
            style={{
              display: "inline-block",
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: pill.bg,
              color: pill.color,
              padding: "2px 8px",
              borderRadius: 4,
              marginBottom: 8,
            }}
          >
            {status.badge}
          </span>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1e293b",
              marginBottom: 10,
            }}
          >
            {status.label}
          </p>
          <p
            style={{
              fontSize: 10,
              color: "#94a3b8",
              fontWeight: 600,
              marginBottom: 3,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {status.criteriaLabel || scale.criteriaLabel}
          </p>
          <p style={{ fontSize: 11.5, color: "#334155", lineHeight: 1.5 }}>
            {status.description}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Next-appointment cell: formatted date or a "None scheduled" placeholder.
 *
 * @param patient - The patient row.
 * @returns The next-appointment cell content
 */
export function NextAppointmentCell({ patient }: { patient: Patient }) {
  if (!patient.nextAppointment) {
    return (
      <span className="text-xs" style={{ color: "#9CA3AF" }}>
        None scheduled
      </span>
    );
  }
  const appt = new Date(patient.nextAppointment);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((appt.getTime() - today.getTime()) / 86_400_000);
  const relative =
    days < 0
      ? "Past"
      : days === 0
        ? "Today"
        : days === 1
          ? "Tomorrow"
          : `In ${days} days`;
  const isSoon = days >= 0 && days <= 3;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span
        className="text-sm font-medium"
        style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}
      >
        {appt.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })}
      </span>
      <span
        className="text-xs"
        style={{ color: isSoon ? "#B45309" : "#9CA3AF" }}
      >
        {relative}
      </span>
    </div>
  );
}

/**
 * ECOG cell: color-coded performance-status score badge (0 best → 4 worst), or a
 * dash when no assessment is available.
 *
 * @param patient - The patient row (carries `ecogScore` from the notification row).
 * @returns The ECOG badge content
 */
export function EcogCell({ patient }: { patient: Patient }) {
  const score = (patient as Patient & { ecogScore?: number | null }).ecogScore;
  if (score === null || score === undefined) {
    return (
      <span className="text-xs font-medium" style={{ color: "#666666" }}>
        -
      </span>
    );
  }
  const palette: Record<number, { bg: string; color: string }> = {
    0: { bg: "#D1FAE5", color: "#065F46" },
    1: { bg: "#DBEAFE", color: "#1E40AF" },
    2: { bg: "#FEF3C7", color: "#92400E" },
  };
  const style = palette[score] ?? { bg: "#FEE2E2", color: "#991B1B" };
  return (
    <span
      className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-md w-fit whitespace-nowrap"
      style={{
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.color}33`,
      }}
      title="ECOG performance status"
    >
      ECOG {score}
    </span>
  );
}

/**
 * Insurance cell: show the normalized category when available, otherwise the
 * payer name. Renders a muted dash when no coverage is on file.
 *
 * @param patient - The patient row.
 * @returns The insurance cell content
 */
export function InsuranceCell({ patient }: { patient: Patient }) {
  const lineOfBusiness = patient.insuranceCategory;
  const payer = patient.insurance;
  if (!lineOfBusiness && !payer) {
    return (
      <span className="text-sm font-medium" style={{ color: "#9CA3AF" }}>
        —
      </span>
    );
  }
  return (
    <div
      className="text-xs truncate text-center"
      title={[lineOfBusiness, payer].filter(Boolean).join(" · ")}
    >
      <span className="font-semibold" style={{ color: "#000000" }}>
        {lineOfBusiness || payer}
      </span>
    </div>
  );
}

/**
 * Document-name cell: the latest report title (from FHIR) for the active tab's
 * document type. Falls back to a muted dash when no title was captured.
 *
 * @param patient - The patient row.
 * @param documentType - The active tab's document type (selects which doc name to show).
 * @returns The document-name cell content
 */
export function DocumentNameCell({
  patient,
  documentType,
}: {
  patient: Patient;
  documentType: DocumentType;
}) {
  // Prefer the expanded row's OWN document name (so two same-date reports show distinct
  // titles); fall back to the type-level latest name for un-expanded rows.
  const name =
    patient.documentName ||
    (patient.documentNamesByType || {})[toBackendDocType(documentType)];
  if (!name) {
    return (
      <span className="text-sm font-medium" style={{ color: "#9CA3AF" }}>
        —
      </span>
    );
  }
  return (
    <div
      className="text-xs font-medium"
      style={{ color: "#000000", lineHeight: "1.4" }}
      title={name}
    >
      {name}
    </div>
  );
}

/**
 * Patient-tags cell: renders the CRC's free-form tags as removable chips plus an
 * inline "+" control to add a new one. Mutations are pushed up via `onPatientUpdate`
 * (which persists them). Clicks are contained so they never trigger row navigation.
 *
 * @param patient - The patient row.
 * @param onPatientUpdate - Callback to apply + persist the updated tag list.
 * @returns The patient-tags cell content
 */
export function PatientTagsCell({
  patient,
  onPatientUpdate,
}: {
  patient: Patient;
  onPatientUpdate?: (patient: Patient) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const tags = patient.patientTags || [];

  const commit = (next: string[]) =>
    onPatientUpdate?.({ ...patient, patientTags: next });

  const addTag = () => {
    const trimmed = value.trim();
    if (
      trimmed &&
      !tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())
    ) {
      commit([...tags, trimmed]);
    }
    setValue("");
    setOpen(false);
  };

  const removeTag = (tag: string, e: React.MouseEvent) => {
    e.stopPropagation();
    commit(tags.filter((t) => t !== tag));
  };

  return (
    <div
      className="flex flex-col items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {tags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{
                backgroundColor: "#EFF6FF",
                color: "#1E40AF",
                border: "1px solid #BFDBFE",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              {tag}
              <button
                onClick={(e) => removeTag(tag, e)}
                aria-label={`Remove tag ${tag}`}
                style={{
                  lineHeight: 1,
                  color: "#3B82F6",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {open ? (
        <div className="flex items-center gap-1">
          <input
            type="text"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              } else if (e.key === "Escape") {
                setValue("");
                setOpen(false);
              }
            }}
            placeholder="Add a tag…"
            className="text-xs px-2 py-1 rounded border outline-none"
            style={{ borderColor: "var(--color-border)", width: "96px" }}
          />
          <button
            onClick={addTag}
            aria-label="Confirm tag"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full"
            style={{
              backgroundColor: "#D1FAE5",
              color: "#059669",
              border: "none",
              cursor: "pointer",
            }}
          >
            ✓
          </button>
          <button
            onClick={() => {
              setValue("");
              setOpen(false);
            }}
            aria-label="Cancel"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full"
            style={{
              backgroundColor: "#FEE2E2",
              color: "#DC2626",
              border: "none",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          title="Add a tag"
          aria-label="Add a tag"
          className="inline-flex items-center justify-center w-5 h-5 text-sm font-medium rounded border border-dashed hover:bg-gray-50 transition-colors"
          style={{
            borderColor: "var(--color-border)",
            color: "var(--color-text-secondary)",
            lineHeight: 1,
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

/**
 * Alerts cell: renders an AI-generated alert string, or a bulleted list when the
 * alerts are provided as an array.
 *
 * @param patient - The patient row.
 * @returns The alerts cell content
 */
export function AlertsCell({ patient }: { patient: Patient }) {
  if (Array.isArray(patient.alerts)) {
    if (patient.alerts.length === 0) {
      return (
        <span className="text-sm font-medium" style={{ color: "#9CA3AF" }}>
          —
        </span>
      );
    }
    return (
      <ul
        className="list-disc list-outside ml-4 space-y-1 text-xs font-medium"
        style={{ color: "#000000" }}
      >
        {patient.alerts.map((alert, index) => (
          <li key={index} style={{ lineHeight: "1.4" }}>
            {alert}
          </li>
        ))}
      </ul>
    );
  }
  if (!patient.alerts) {
    return (
      <span className="text-sm font-medium" style={{ color: "#9CA3AF" }}>
        —
      </span>
    );
  }
  return (
    <div
      className="text-xs font-medium"
      style={{ color: "#000000", lineHeight: "1.4" }}
    >
      {patient.alerts}
    </div>
  );
}
